import { SecShape, type SecondaryDef } from '../../sim/secondary';

/** S05 — RIPOSTE RAPPROCHÉE. Le second angle mort structurel : ce qui est
 *  DÉJÀ passé.
 *
 *  ── Le besoin ────────────────────────────────────────────────────────────
 *  Les quatre premières armes couvrent le haut (Lance, Faisceau, Mines) et la
 *  bande horizontale exacte du vaisseau (Flanqueurs). Personne ne couvre les
 *  diagonales ni le dessous. Or deux ennemis vivent précisément là :
 *
 *    · le DARD plonge à 720 u/s et, s'il rate, poursuit sa course sous le
 *      vaisseau — c'est un kamikaze de 60 PV que plus rien n'atteint alors
 *      qu'il est encore vivant et remontera dans le compte de la vague ;
 *    · la MITE traverse vers le bas en groupes de 5 à 9, et un groupe qui a
 *      dépassé la ligne de tir traverse le reste du terrain gratuitement.
 *
 *  S'y ajoute une propriété du jeu qui rend le cas fréquent : le bullet sealing
 *  (§6.5) éteint le tir d'un ennemi sous 150 u. À bout portant, l'ennemi ne
 *  tire plus — le danger n'est plus le projectile, c'est le CORPS. Aucune arme
 *  du catalogue ne traite un problème de corps à moins de 150 u.
 *
 *  ── Ce qui la distingue des Flanqueurs ───────────────────────────────────
 *  Les deux achètent du temps de replacement, et la confusion serait facile.
 *  L'écart est dans la nature de la menace :
 *
 *    · les Flanqueurs répondent à une menace PRÉVUE — le Rôdeur traverse à
 *      hauteur constante, on le voit venir, on tire à portée (1 300 u/s, cône
 *      étroit de 26°) ;
 *    · la Riposte répond à une menace qui est déjà là, dans une direction que
 *      le joueur n'a pas choisie. Elle ne vise pas : elle dégage.
 *
 *  ── Pourquoi elle n'a pas besoin d'un champ de portée ────────────────────
 *  Le moteur n'a aucune notion de portée, et il n'en faut aucune : dix
 *  projectiles en éventail sont denses à 60 u du vaisseau et clairsemés à 400.
 *  L'arme se dilue toute seule avec la distance — la géométrie fait le travail
 *  qu'un champ supplémentaire aurait fait moins bien, et sans coûter un octet
 *  dans le pool.
 *
 *  ── L'équilibrage ────────────────────────────────────────────────────────
 *  80 DPS théoriques, 40 % de la primaire : le plus bas du catalogue, sous les
 *  Flanqueurs. C'est cohérent et c'est voulu — une arme de dégagement dont les
 *  dégâts seraient compétitifs serait déclenchée pour ses dégâts, donc jamais
 *  disponible au moment où il faut dégager. Et le chiffre théorique est ici
 *  particulièrement optimiste : il suppose que les dix projectiles trouvent
 *  quelque chose dans dix directions à la fois, ce qui n'arrive jamais. */
const riposte: SecondaryDef = {
  id: 'riposte-rapprochee',
  name: 'Riposte rapprochée',
  shape: SecShape.Flanc,
  // 3,5 s. Plus court que tout sauf les Flanqueurs : une arme réactive dont la
  // recharge est longue n'est jamais prête quand la situation se présente, et
  // le joueur reprend l'habitude de fuir — l'arme existerait sans être jouée.
  cooldown: 3.5,
  shots: 1,
  shotEvery: 0,
  // Par côté : la couronne compte donc dix charges.
  count: 5,
  // 22 u du fuselage, contre 34 pour les Flanqueurs, et l'écart est le sujet :
  // les drones doivent se lire comme deux essaims DISTINCTS du vaisseau, la
  // Riposte doit se lire comme une décharge qui en ÉMANE. La même donnée porte
  // deux intentions opposées.
  spacing: 22,
  // ±85° autour de l'horizontale. Presque une couronne fermée : il reste 10°
  // de part et d'autre de la verticale exacte, refermés en pratique par le
  // rayon des projectiles.
  //
  // 180° aurait été le réflexe, et aurait cassé l'arme : à 180 les projectiles
  // extrêmes partent exactement à la verticale, leur composante latérale
  // s'annule, et une salve « de flanc » qui ne couvre plus les flancs qu'avec
  // son projectile médian ne couvre plus rien.
  spreadDeg: 170,
  // Nettement au-dessus des projectiles ennemis (380-620 u/s) pour que la
  // hiérarchie de lecture tienne, nettement sous la primaire (1 800) parce
  // qu'une couronne rapide serait sortie du champ utile avant d'avoir été vue.
  speed: 1100,
  radius: 18,
  damage: 28,
  text: '2 × 5 × 28 dégâts en couronne autour du vaisseau — 3,5 s',
};

export default riposte;
